import { a11yProps, palette as P } from './RoadrunnerMark';

type Props = { className?: string; title?: string };

const line = {
  stroke: P.ink,
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

// Same head as the brand mark; body leveled out and legs mid-stride.
const TAIL = 'M18.73 27.33 L-3.33 17.82 C-6.16 16.82 -8.7 23.98 -5.87 24.98 L17.27 31.47 Z';
const BODY =
  'M50.4 18.3 C51.4 16.9 51.2 14.6 50.2 13.4 C49.4 10.4 45.8 9.4 43 10.2 Q40 7.8 36.6 6.8 Q38.8 9.4 40.2 11.4 Q37.4 10.2 34.2 10.6 Q37.2 12.2 39.6 14 Q37.6 14.4 35.4 15.6 Q38.4 15.8 40.6 16.6 C39.6 20 37 22.8 33 24.4 C28 26.2 22 26.6 17 27.4 C14.4 27.8 13.8 31.6 16.4 32.8 C22 35.6 31 35.4 37 32.4 C41.6 30 44.4 26.2 45.4 23 C46.4 20.6 48 19 50.4 18.3 Z';

/** The roadrunner at full tilt with a folded note in its beak (mark coordinates). */
function RunningRoadrunner({ transform }: { transform?: string }) {
  return (
    <g transform={transform}>
      {/* legs */}
      <g fill="none" {...line} strokeWidth={1.65}>
        <path d="M25.6 33.8 L21.4 42.4 L13.4 49.2 M13.4 49.2 L16.8 50.6 M13.4 49.2 L10.2 49.6" />
        <path d="M30.4 33.6 L30 42.6 L39.4 46.2 M39.4 46.2 L42.8 44.8 M39.4 46.2 L37.4 48.8" />
      </g>
      {/* tail streaming back */}
      <path d={TAIL} fill={P.bodyDark} />
      <path d="M-0.58 19.01 L-3.33 17.82 C-6.16 16.82 -8.7 23.98 -5.87 24.98 L-2.98 25.79 Z" fill={P.cream} />
      <path d="M14.2 28.1 L-4.2 21.6" stroke={P.ink} strokeWidth={0.7} strokeLinecap="round" />
      <path d={TAIL} fill="none" {...line} />
      {/* the folded note, clamped in the beak */}
      <g {...line} strokeWidth={1.3}>
        <path d="M54.6 16.4 L67.8 18.8 L66 28.6 L52.8 26.2 Z" fill={P.card} />
        <path d="M54.6 16.4 L60.2 23.6 L67.8 18.8" fill="none" stroke={P.inkSoft} strokeWidth={1} />
      </g>
      {/* beak */}
      <path d="M49.6 13.8 Q56.5 13.9 62.6 17.8 Q56.5 18.3 49.6 18.2 Z" fill={P.inkSoft} {...line} />
      {/* body, neck, head */}
      <path d={BODY} fill={P.body} />
      <path
        d="M50.2 18.4 C48 19 46.4 20.6 45.4 23 C44.4 26.2 41.6 30 37 32.4 C31 35.4 22 35.6 16.6 32.9 C24 33.4 32 32 37 28.6 C40.6 26.2 42.6 23 43.8 20.6 C45.2 19.2 47.6 18.4 50.2 18.4 Z"
        fill={P.cream}
      />
      <path
        d="M50 12.8 C49.4 10.4 45.8 9.4 43 10.2 Q40 7.8 36.6 6.8 Q38.8 9.4 40.2 11.4 Q37.4 10.2 34.2 10.6 Q37.2 12.2 39.6 14 Q37.6 14.4 35.4 15.6 Q38.4 15.8 40.6 16.6 C42 14.4 44.4 12.6 47.4 12.4 Z"
        fill={P.bodyDark}
      />
      <path
        d="M34.6 24.6 C29 27.6 22.6 28.8 17.2 29.6 C22 32.6 31 32.4 36.8 29 C37 27.2 36.2 25.6 34.6 24.6 Z"
        fill={P.bodyDark}
      />
      <path d="M33.6 27.4 l-2.2 1.1 M27.8 30.4 l-2.4 0.6" stroke={P.cream} strokeWidth={1} strokeLinecap="round" />
      <path d="M46.6 21.6 l-1 1.8 M44.4 25.6 l-1.2 1.6" stroke={P.body} strokeWidth={0.9} strokeLinecap="round" />
      <path d={BODY} fill="none" {...line} />
      {/* eye and bare skin patch */}
      <path d="M43.2 14.4 C42 14.2 40.9 14.8 40.3 15.8 C41.2 16.3 42.3 16.3 43.2 15.9 Z" fill={P.adobe} />
      <path d="M45.4 13.9 C44.2 13.7 43 14.2 42.4 15.4 C43.4 16 44.6 16 45.6 15.4 Z" fill={P.sky} />
      <circle cx={46.6} cy={14.4} r={1.5} fill={P.ink} />
      <circle cx={47.1} cy={13.9} r={0.45} fill={P.cream} />
    </g>
  );
}

const PUFFS: [number, number, number][] = [
  [66, 119, 6.4],
  [56, 116, 5],
  [48, 121, 4.4],
  [38, 118, 3],
  [29, 121, 2],
];

/** Success state after posting: the roadrunner dashing off with the note. */
export function NoteCarrier({ className, title }: Props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 220 140"
      width="100%"
      className={className}
      style={{ maxWidth: 260, display: 'block' }}
      {...a11yProps(title)}
    >
      {title && <title>{title}</title>}
      <circle cx={150} cy={50} r={34} fill={P.mustardSoft} />
      {/* ground and shadow */}
      <ellipse cx={122} cy={125} rx={50} ry={3.6} fill={P.board} />
      <path
        d="M24 125 H62 M178 125 H204"
        fill="none"
        stroke={P.inkSoft}
        strokeWidth={2}
        strokeLinecap="round"
        opacity={0.55}
      />
      {/* speed lines */}
      <path
        d="M10 52 H32 M18 64 H38 M8 76 H26"
        fill="none"
        stroke={P.inkSoft}
        strokeWidth={2.2}
        strokeLinecap="round"
        opacity={0.5}
      />
      {/* dust kicked up behind */}
      <g stroke={P.inkSoft} strokeWidth={3.6} fill={P.inkSoft} opacity={0.55}>
        {PUFFS.map(([cx, cy, r]) => (
          <circle key={cx} cx={cx} cy={cy} r={r} />
        ))}
      </g>
      <g fill={P.board}>
        {PUFFS.map(([cx, cy, r]) => (
          <circle key={cx} cx={cx} cy={cy} r={r} />
        ))}
      </g>
      <RunningRoadrunner transform="translate(58 12) scale(2.1)" />
    </svg>
  );
}
